import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Loader2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { getSupportContext } from "@/services/supportChatService";

const REGIME_LABEL: Record<string, string> = {
  minimum: "IVA mínimo (Literal E)",
  cede: "Régimen General — CEDE",
  no_cede: "Régimen General — NO CEDE",
  servicios_personales: "Servicios personales",
};

const BENEFIT_LABEL: Record<string, string> = {
  art5_provider_discount: "Art. 5 — descuento en nuestra factura",
  art4_client_self_credit: "Art. 4 — lo recupera el cliente",
  // Not a direction the credit flows in, but an ANSWER: the client was asked
  // and declined. It renders as an ordinary badge rather than the "sin datos"
  // warning, which must keep meaning "we genuinely do not know".
  none_declined: "Declaró no cumplir",
};

function Row({
  label,
  value,
}: {
  label: string;
  value: string | null;
}): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-right text-xs font-medium text-foreground">
        {value ?? "—"}
      </span>
    </div>
  );
}

/**
 * The reason this exists rather than a vendor console: it answers the
 * fiscal question in front of the operator while they type.
 *
 * Everything under "Perfil fiscal" is tenant-editable, so it is labelled
 * as declared rather than verified. An operator who reads a self-declared
 * regime as fact can promise a benefit the tenant does not qualify for —
 * the same trap the project rule about fiscal-profile fields warns about
 * for billing logic.
 */
export function SupportContextPanel({
  conversationId,
  onUseReply,
}: {
  conversationId: string | null;
  /** Inserts a suggested reply into the composer. */
  onUseReply?: (body: string) => void;
}): JSX.Element {
  const context = useQuery({
    queryKey: ["support-context", conversationId],
    queryFn: () => getSupportContext(conversationId as string),
    enabled: conversationId !== null,
  });

  if (!conversationId) {
    return (
      <div className="rounded-lg border border-border bg-card p-4">
        <p className="text-xs text-muted-foreground">
          Elegí una conversación para ver el contexto del cliente.
        </p>
      </div>
    );
  }

  if (context.isLoading) {
    return (
      <div className="flex h-32 items-center justify-center rounded-lg border border-border bg-card">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const visitor = context.data?.visitor ?? null;
  if (visitor) {
    /*
      A visitor is a prospect, and what an operator needs before
      answering one is who they are and whether we can reach them —
      not the empty "sin empresa asociada" this used to render, which
      restated what the thread already showed.
    */
    return (
      <div className="space-y-4 rounded-lg border border-border bg-card p-4">
        <div>
          <p className="text-sm font-semibold text-foreground">
            {visitor.name ?? "Visitante sin nombre"}
          </p>
          <p className="text-xs text-muted-foreground">
            Sin cuenta en Fiscalo
          </p>
        </div>
        <div className="border-t border-border pt-2">
          <Row label="Email" value={visitor.email} />
          <Row
            label="Primera visita"
            value={new Date(visitor.firstSeenAt).toLocaleDateString("es-UY", {
              day: "numeric",
              month: "short",
              year: "numeric",
            })}
          />
        </div>
        {visitor.linkedUserId ? (
          <Badge variant="secondary">Ya creó su cuenta</Badge>
        ) : !visitor.email ? (
          /*
            The single most actionable fact on this panel: with no
            address, an answer only reaches them while this tab is open.
            Worth an operator knowing BEFORE they write three paragraphs.
          */
          <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            No dejó email: sólo lo alcanzás mientras tenga la página
            abierta.
          </p>
        ) : null}
      </div>
    );
  }

  const data = context.data?.tenant ?? null;
  if (!data) {
    return (
      <div className="rounded-lg border border-border bg-card p-4">
        <p className="text-xs text-muted-foreground">
          Sin contexto para esta conversación.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 rounded-lg border border-border bg-card p-4">
      <div>
        <p className="text-sm font-semibold text-foreground">
          {data.legalName ?? data.name}
        </p>
        <p className="text-xs text-muted-foreground">RUT {data.rut}</p>
      </div>

      <div className="border-t border-border pt-2">
        <Row label="Plan" value={data.planId} />
        <Row
          label="Inicio de actividad"
          value={
            data.activityStartDate
              ? new Date(data.activityStartDate).toLocaleDateString("es-UY")
              : null
          }
        />
        <Row
          label="Literal E verificado"
          value={
            data.literalEVerified === null
              ? "sin verificar"
              : data.literalEVerified
                ? "sí"
                : "no"
          }
        />
      </div>

      <div className="border-t border-border pt-2">
        <div className="mb-1 flex items-center gap-1">
          <span className="text-xs font-semibold text-foreground">
            Perfil fiscal
          </span>
          <Badge variant="outline" className="text-[10px]">
            declarado
          </Badge>
        </div>
        <Row
          label="Régimen IVA"
          value={
            data.fiscalProfile?.ivaRegime
              ? (REGIME_LABEL[data.fiscalProfile.ivaRegime] ??
                data.fiscalProfile.ivaRegime)
              : null
          }
        />
        <Row
          label="Tipo de empresa"
          value={data.fiscalProfile?.companyType ?? null}
        />
        <Row
          label="Opt-in beneficios"
          value={
            data.fiscalProfile?.fiscalBenefitOptIn === null ||
            data.fiscalProfile?.fiscalBenefitOptIn === undefined
              ? "no declarado"
              : data.fiscalProfile.fiscalBenefitOptIn
                ? "sí"
                : "no"
          }
        />
      </div>

      <div className="border-t border-border pt-2">
        <p className="mb-1 text-xs font-semibold text-foreground">
          Crédito 80 UI (Dec. 206/019)
        </p>
        {data.benefitDirection ? (
          <Badge variant="outline" className="mb-2 text-[10px]">
            {BENEFIT_LABEL[data.benefitDirection] ?? data.benefitDirection}
          </Badge>
        ) : (
          <div className="mb-2 flex items-start gap-1 text-[11px] text-destructive">
            <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
            <span>Sin datos suficientes</span>
          </div>
        )}
        <p className="text-[11px] leading-snug text-muted-foreground">
          {data.benefitDirectionReason}
        </p>
      </div>

      {data.suggestedReplies.length > 0 && onUseReply ? (
        <div className="border-t border-border pt-2">
          <p className="mb-2 text-xs font-semibold text-foreground">
            Respuestas sugeridas
          </p>
          <div className="space-y-1">
            {data.suggestedReplies.map((reply) => (
              <button
                key={reply.id}
                type="button"
                onClick={() => onUseReply(reply.body)}
                title={reply.body}
                className="w-full rounded-sm border border-border px-2 py-1 text-left text-[11px] text-foreground transition-colors hover:bg-muted"
              >
                {reply.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
